import {
  ArrowRight,
  Briefcase,
  Building2,
  Calendar,
  CalendarClock,
  CalendarPlus,
  ChevronRight,
  Pencil,
  Users,
  X,
  type LucideIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useEmployeeAuditPreview } from '@/hooks/use-employee-audit-log';
import { formatDateTime } from '@/lib/date';
import { cn } from '@/lib/utils';
import type { EmployeeAuditEntry } from '@/types/employee-audit';
import {
  describeActor,
  describeEntry,
  historyTimelineStyle,
  type HistoryTimelineIcon,
  type HistoryTimelineTone,
} from '@/features/people/lib/employee-history';

const ICONS: Record<HistoryTimelineIcon, LucideIcon> = {
  created: CalendarPlus,
  deleted: X,
  lifecycle: CalendarClock,
  role: Briefcase,
  org: Building2,
  people: Users,
  date: Calendar,
  detail: Pencil,
};

// The old Position & History timeline's palette: amber for probation and
// termination events, green for the rest.
const TONES: Record<HistoryTimelineTone, { dot: string; icon: string }> = {
  event: { dot: 'border-amber-400 bg-amber-400', icon: 'text-amber-500' },
  change: { dot: 'border-emerald-500 bg-emerald-500', icon: 'text-emerald-500' },
  danger: { dot: 'border-red-500 bg-red-500', icon: 'text-red-500' },
};

/** createdAt is an instant, so its calendar day is the viewer's local one. */
const DAY: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' };

/**
 * The newest changes on the Job tab's "Position & History" card, in the
 * History tab's order, drawn as the card's old timeline [HW-795]. Mount it
 * only for holders of employees.audit.view: it requests the audit log on mount.
 */
export function HistoryPreview({
  employeeId,
  onShowAll,
}: {
  employeeId: string;
  onShowAll?: () => void;
}) {
  const { entries, hasMore, isLoading, error, refetch, isRefetchError, isRefetching } =
    useEmployeeAuditPreview(employeeId);

  return (
    <div>
      <h4 className="mb-4 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        History
      </h4>
      {isLoading ? (
        <p role="status" className="text-sm text-muted-foreground">
          Loading...
        </p>
      ) : error && entries.length === 0 ? (
        <p role="alert" className="text-sm text-destructive">
          Failed to load the history.
        </p>
      ) : entries.length === 0 ? (
        <p className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
          No changes recorded yet.
        </p>
      ) : (
        <>
          {/* While a refetch runs, isRefetchError still holds the previous failure,
              so the notice waits for it to settle. */}
          {isRefetchError && !isRefetching && (
            <div className="mb-4 flex items-center gap-2 text-xs text-destructive">
              <span role="alert">Couldn't refresh the history.</span>
              <Button variant="ghost" size="sm" onClick={() => refetch()} className="text-xs">
                Retry
              </Button>
            </div>
          )}
          <div className="relative pl-6">
            {/* Timeline line */}
            <div className="absolute bottom-2 left-[7px] top-2 w-0.5 bg-border" />
            <ul className="flex flex-col gap-3">
              {entries.map((e, i) => (
                <TimelineItem key={e.id} entry={e} newest={i === 0} />
              ))}
            </ul>
          </div>
          {hasMore && onShowAll && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onShowAll}
              className="mt-1 h-7 gap-1 px-2 text-xs text-primary"
            >
              Show all history
              <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
            </Button>
          )}
        </>
      )}
    </div>
  );
}

/** One entry as a card on the timeline; the newest is highlighted as the old current position was. */
function TimelineItem({ entry, newest }: { entry: EmployeeAuditEntry; newest: boolean }) {
  const change = describeEntry(entry);
  const who = describeActor(entry);
  const style = historyTimelineStyle(entry);
  const Icon = ICONS[style.icon];
  const tone = TONES[style.tone];
  return (
    <li className="relative">
      <div
        className={cn(
          'absolute -left-6 top-1.5 h-3.5 w-3.5 rounded-full border-2 transition-all',
          tone.dot
        )}
      />
      <Card
        className={cn(
          'p-3 transition-all duration-200',
          newest
            ? 'border-primary/40 bg-primary/5 shadow-sm'
            : 'border-border/50 bg-muted/30 hover:border-border hover:shadow-sm'
        )}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 flex-1 items-start gap-2">
            <Icon className={cn('mt-px h-3.5 w-3.5 shrink-0', tone.icon)} aria-hidden="true" />
            <div className="min-w-0">
              <div className="truncate text-xs font-semibold text-foreground">{change.title}</div>
              {change.from !== undefined && (
                <div className="mt-0.5 flex flex-wrap items-center gap-x-1.5 text-[11px] text-muted-foreground">
                  <span className="sr-only">from </span>
                  <del className="wrap-anywhere line-through">{change.from}</del>
                  <ArrowRight className="h-3 w-3 shrink-0" aria-hidden="true" />
                  <span className="sr-only">to </span>
                  <ins className="wrap-anywhere text-foreground no-underline">{change.to}</ins>
                </div>
              )}
              <div className="wrap-anywhere mt-0.5 text-[11px] text-muted-foreground">{who}</div>
            </div>
          </div>
          <time
            dateTime={entry.createdAt}
            title={formatDateTime(entry.createdAt)}
            className={cn(
              'shrink-0 whitespace-nowrap text-[11px] font-medium',
              newest ? 'font-semibold text-primary' : 'text-muted-foreground'
            )}
          >
            {formatDateTime(entry.createdAt, DAY)}
          </time>
        </div>
      </Card>
    </li>
  );
}
